import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const statusColor = {
  new: { bg: "#ebe8e0", fg: "#3c3c3c" },
  qualified: { bg: "#d4a373", fg: "#163325" },
  contacted: { bg: "#8c9b86", fg: "#fff" },
  booked: { bg: "#163325", fg: "#f9f8f6" },
  won: { bg: "#0e2419", fg: "#d4a373" },
  lost: { bg: "#efece5", fg: "#5c5c5c" },
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leads").then((r) => setLeads(r.data.leads)).finally(() => setLoading(false));
  }, []);

  const filtered = leads.filter(
    (l) => !q || l.name?.toLowerCase().includes(q.toLowerCase()) || l.email?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="p-8 lg:p-10" data-testid="dashboard-leads">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#8c9b86]">Pipeline</div>
          <h1 className="font-serif-display text-4xl mt-2">Leads</h1>
        </div>
        <Input
          placeholder="Search leads…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="leads-search-input"
          className="max-w-xs bg-white border-[#e5e2da] focus-visible:ring-[#163325]"
        />
      </div>

      <div className="mt-8 rounded-lg border border-[#e5e2da] bg-white overflow-hidden">
        <Table data-testid="leads-table">
          <TableHeader>
            <TableRow className="bg-[#efece5] hover:bg-[#efece5]">
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Captured</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} className="text-center text-[#5c5c5c] py-8">Loading…</TableCell></TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-[#5c5c5c] py-12" data-testid="leads-empty">No leads yet. Test the chatbot on the landing page to capture one.</TableCell></TableRow>
            )}
            {filtered.map((l) => {
              const c = statusColor[l.status] || statusColor.new;
              return (
                <TableRow key={l.id} className="hover:bg-[#f9f8f6]" data-testid={`lead-row-${l.id}`}>
                  <TableCell>
                    <Link to={`/app/leads/${l.id}`} className="font-medium text-[#163325] hover:underline underline-offset-4">
                      {l.name}
                    </Link>
                    {l.summary && <div className="text-xs text-[#5c5c5c] mt-1">{l.summary}</div>}
                  </TableCell>
                  <TableCell className="text-sm text-[#3c3c3c]">{l.email}</TableCell>
                  <TableCell className="text-sm capitalize">{l.source?.replace("_", " ")}</TableCell>
                  <TableCell className="text-sm" data-testid={`lead-channel-${l.id}`}>
                    {l.utm_source ? <span className="inline-block rounded-full bg-[#163325] text-[#f9f8f6] px-2 py-0.5 text-xs">{l.utm_source}</span> : <span className="text-[#8c9b86] text-xs">direct</span>}
                  </TableCell>
                  <TableCell>
                    <Badge className="capitalize" style={{ background: c.bg, color: c.fg, fontWeight: 500 }}>{l.status}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-[#5c5c5c]">{l.created_at?.slice(0, 10)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
